import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { FileUploadField } from "./FileUploadField";

export interface FieldConfig {
  id: string;
  field_key: string;
  label: string;
  field_type: string;
  placeholder?: string | null;
  helper_text?: string | null;
  unit?: string | null;
  is_required: boolean;
  options?: { value: string; label: string }[] | null;
  validation?: Record<string, unknown> | null;
  default_value?: string | null;
  category?: string | null;
  scan_template_id?: string | null;
}

interface DynamicFieldProps {
  field: FieldConfig;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  error?: string;
  dealId?: string | null;
}

function formatCurrency(val: string): string {
  const num = parseFloat(val.replace(/[^0-9.-]/g, ""));
  if (isNaN(num)) return val;
  return num.toLocaleString("en-US");
}

export function DynamicField({ field, value, onChange, error, dealId }: DynamicFieldProps) {
  const [displayValue, setDisplayValue] = useState<string>("");
  const [focused, setFocused] = useState(false);

  const isCurrency = field.unit === "$" || field.unit === "$/unit" || field.unit === "$/unit/yr";
  const isPercent = field.unit === "%";
  const prefix = isCurrency ? "$" : undefined;
  const suffix = isPercent ? "%" : field.unit && !isCurrency ? field.unit : undefined;

  const renderLabel = () => (
    <div className="flex items-center gap-1 mb-1.5">
      <Label htmlFor={field.field_key} className="text-sm font-medium text-foreground">
        {field.label}
      </Label>
      {field.is_required && <span className="text-destructive text-xs">*</span>}
    </div>
  );

  const renderHelper = () =>
    field.helper_text ? (
      <p className="text-xs text-muted-foreground mt-1">{field.helper_text}</p>
    ) : null;

  const renderError = () =>
    error ? <p className="text-xs text-destructive mt-1">{error}</p> : null;

  switch (field.field_type) {
    case "text":
      return (
        <div>
          {renderLabel()}
          <Input
            id={field.field_key}
            placeholder={field.placeholder || ""}
            value={(value as string) || ""}
            onChange={(e) => onChange(field.field_key, e.target.value)}
          />
          {renderHelper()}
          {renderError()}
        </div>
      );

    case "textarea":
      return (
        <div>
          {renderLabel()}
          <Textarea
            id={field.field_key}
            placeholder={field.placeholder || ""}
            value={(value as string) || ""}
            onChange={(e) => onChange(field.field_key, e.target.value)}
          />
          {renderHelper()}
          {renderError()}
        </div>
      );

    case "number": {
      const rawVal = value != null ? String(value) : "";
      const display = focused ? rawVal : rawVal ? formatCurrency(rawVal) : "";

      return (
        <div>
          {renderLabel()}
          <div className="relative">
            {prefix && (
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                {prefix}
              </span>
            )}
            <Input
              id={field.field_key}
              type={focused ? "number" : "text"}
              placeholder={field.placeholder || ""}
              value={display}
              className={prefix ? "pl-7" : suffix ? "pr-10" : ""}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onChange={(e) => {
                const v = e.target.value;
                onChange(field.field_key, v === "" ? "" : v);
              }}
            />
            {suffix && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                {suffix}
              </span>
            )}
          </div>
          {renderHelper()}
          {renderError()}
        </div>
      );
    }

    case "select": {
      const opts = (field.options || []) as { value: string; label: string }[];
      return (
        <div>
          {renderLabel()}
          <Select
            value={(value as string) || ""}
            onValueChange={(v) => onChange(field.field_key, v)}
          >
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder || "Select..."} />
            </SelectTrigger>
            <SelectContent>
              {opts.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {renderHelper()}
          {renderError()}
        </div>
      );
    }

    case "multi_select": {
      const opts = (field.options || []) as { value: string; label: string }[];
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div>
          {renderLabel()}
          <div className="space-y-2 rounded-md border border-input p-3">
            {opts.map((o) => (
              <label key={o.value} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={selected.includes(o.value)}
                  onCheckedChange={(checked) => {
                    const next = checked
                      ? [...selected, o.value]
                      : selected.filter((v) => v !== o.value);
                    onChange(field.field_key, next);
                  }}
                />
                <span className="text-sm">{o.label}</span>
              </label>
            ))}
          </div>
          {renderHelper()}
          {renderError()}
        </div>
      );
    }

    case "checkbox":
      return (
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={!!value}
              onCheckedChange={(checked) => onChange(field.field_key, !!checked)}
            />
            <span className="text-sm font-medium">{field.label}</span>
            {field.is_required && <span className="text-destructive text-xs">*</span>}
          </label>
          {renderHelper()}
          {renderError()}
        </div>
      );

    case "toggle":
      return (
        <div className="flex items-center justify-between rounded-md border border-input p-3">
          <div>
            <Label className="text-sm font-medium">{field.label}</Label>
            {field.helper_text && (
              <p className="text-xs text-muted-foreground mt-0.5">{field.helper_text}</p>
            )}
          </div>
          <Switch
            checked={!!value}
            onCheckedChange={(checked) => onChange(field.field_key, checked)}
          />
          {renderError()}
        </div>
      );

    case "file_upload":
      return (
        <FileUploadField
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          dealId={dealId}
        />
      );

    default:
      return (
        <div>
          {renderLabel()}
          <Input
            id={field.field_key}
            placeholder={field.placeholder || ""}
            value={(value as string) || ""}
            onChange={(e) => onChange(field.field_key, e.target.value)}
          />
          {renderHelper()}
          {renderError()}
        </div>
      );
  }
}
